import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  FormControlLabel, 
  Checkbox,
  Paper,
  Divider
} from '@mui/material';
import { API_URL } from '../../config/api';

const validationSchema = yup.object({
  notified: yup.boolean().oneOf([true], 'You must confirm that you have notified your manager and HR').required('You must confirm that you have notified your manager and HR'),
  managerEmail: yup.string().email('Please enter a valid email address').required('Manager email is required')
});

const NotificationStep = ({ onFormSubmit, readOnly = false, userIdOverride }) => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const effectiveUserId = userIdOverride || user?.id;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      notified: false,
      managerEmail: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        let response;
        
        if (isUpdate) {
          // Update existing notification
          const url = `${API_URL}/offboarding/notification/user/${effectiveUserId}`;
          response = await axios.put(url, values, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            }
          });
          toast.success('Notification updated successfully!');
        } else {
          // Create new notification
          const url = `${API_URL}/offboarding/notification`;
          response = await axios.post(url, values, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            }
          });
          toast.success('Notification submitted successfully!');
        }
        
        // After successful submission, fetch the updated data to set update mode
        try {
          const fetchResponse = await axios.get(`${API_URL}/offboarding/notification/user/${effectiveUserId}`, {
            headers: getAuthHeaders()
          });
          
          if (fetchResponse.data) {
            const formattedData = {
              ...fetchResponse.data,
              notificationDate: fetchResponse.data.notificationDate ? new Date(fetchResponse.data.notificationDate).toISOString().split('T')[0] : ''
            };
            formik.setValues(formattedData);
            setIsUpdate(true);
            console.log('Form updated to update mode after submission');
            
            // Trigger validation update in parent component
            if (onFormSubmit) {
              onFormSubmit();
            }
          }
        } catch (fetchError) {
          console.error('Error fetching updated data:', fetchError);
        }
        
      } catch (error) {
        const errorMessage = error.response?.data?.error || `Failed to ${isUpdate ? 'update' : 'submit'} notification`;
        toast.error(errorMessage);
        console.error(`Error ${isUpdate ? 'updating' : 'submitting'} notification:`, error);
      } finally {
        setLoading(false);
      }
    }
  });

  // Load existing notification data and manager email
  useEffect(() => {
    if (effectiveUserId) {
      console.log('Fetching notification data for user:', effectiveUserId);
      
      // First, try to get existing notification data
      axios.get(`${API_URL}/offboarding/notification/user/${effectiveUserId}`, {
        headers: getAuthHeaders()
      })
        .then(response => {
          console.log('GET API Response:', response.data);
          const data = response.data.data || response.data;
          if (data) {
            const formattedData = {
              ...data,
              notificationDate: data.notificationDate ? new Date(data.notificationDate).toISOString().split('T')[0] : ''
            };
            console.log('Formatted data:', formattedData);
            formik.setValues(formattedData);
            setIsUpdate(true);
          } else {
            // If no existing notification, fetch manager email
            fetchManagerEmail();
          }
        })
        .catch(error => {
          console.log('GET API Error:', error.response?.status, error.response?.data);
          if (error.response && error.response.status !== 404) {
            console.error('Error fetching notification form:', error);
            toast.error('Error loading existing form data');
          } else {
            // If 404 (no existing notification), fetch manager email
            fetchManagerEmail();
          }
        });
    }
  }, [effectiveUserId, getAuthHeaders]);

  // Function to fetch manager email from user hierarchy
  const fetchManagerEmail = async () => {
    try {
      console.log('Fetching manager email for user:', user.id);
      const response = await axios.get(`${API_URL}/user-hierarchy/managers/${user.id}`, {
        headers: getAuthHeaders()
      });
      
      console.log('Manager response:', response.data);
      
      if (response.data.managers && response.data.managers.length > 0) {
        // Get the direct manager (first in the list)
        const directManager = response.data.managers[0];
        if (directManager.email) {
          console.log('Setting manager email:', directManager.email);
          formik.setFieldValue('managerEmail', directManager.email);
        }
      }
    } catch (error) {
      console.log('Error fetching manager email:', error.response?.status, error.response?.data);
      // Don't show error toast for this as it's not critical
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2, backgroundColor: 'white' }}>
      <form onSubmit={formik.handleSubmit}>
        <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
          NOTIFICATION
        </Typography>
        
        {/* Notification Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Manager and HR Notification
          </Typography>
          {isUpdate ? (
            <Box sx={{ 
              mb: 3, 
              p: 3, 
              backgroundColor: '#e8f5e8', 
              borderRadius: 2, 
              border: '1px solid #4caf50',
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 1 }}>
                ✓ Notification Sent Successfully
              </Typography>
              <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 'medium' }}>
                Sent on: {formik.values.notificationDate}
              </Typography>
              <Typography variant="body2" sx={{ color: '#2e7d32', mt: 1, fontStyle: 'italic' }}>
                You have already sent notification to your manager ({formik.values.managerEmail}) and HR (hr@divergentinsights.com)
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 3, fontStyle: 'italic' }}>
                Please confirm you have notified your manager and HR by providing the details below.
              </Typography>
              <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Manager Email"
                    name="managerEmail"
                    type="email"
                    value={formik.values.managerEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    fullWidth
                    required
                    error={formik.touched.managerEmail && Boolean(formik.errors.managerEmail)}
                    helperText={formik.touched.managerEmail && formik.errors.managerEmail}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="notified"
                        checked={formik.values.notified}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    }
                    label="I have notified my manager and HR."
                  />
                  {formik.touched.notified && Boolean(formik.errors.notified) && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {formik.errors.notified}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              </fieldset>
            </>
          )}
        </Box>

        {!isUpdate && !readOnly && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ minWidth: 150, py: 1.5 }}
              >
                {loading ? 'Submitting...' : 'Submit Notification'}
              </Button>
            </Box>
          </>
        )}
      </form>
    </Paper>
  );
};

export default NotificationStep; 